Copyright 2022 Bio-Conversion Databank Foundation Licensed under the Apache
License, Version 2.0 (the "License"); you may not use this file except in
compliance with the License. You may obtain a copy of the License at
http://www.apache.org/licenses/LICENSE-2.0 Unless required by applicable law or
agreed to in writing, software distributed under the License is distributed on
an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express
or implied. See the License for the specific language governing permissions and
limitations under the License

<template>
  <v-container>
    <span class="d-flex justify-center mb-5 mt-n6">
      <v-img
        class="ml-2 mr-1"
        max-width="25"
        src="@/assets/icon/phage.svg"
      ></v-img>
      Phage Receptor Database (PhReD)
    </span>

    <!-- Login form -->
    <v-form
      class="pt-12"
      ref="loginForm"
      v-if="showLogin"
      @submit.prevent="onLogin"
      v-model="isLoginFormValid"
    >
      <p>Login to your account</p>

      <v-text-field
        v-model="email"
        label="Email"
        class="mx-2"
        data-testid="email"
        outlined
        clearable
      >
      </v-text-field>

      <v-text-field
        v-model="password"
        label="Password"
        class="mx-2"
        data-testid="password"
        outlined
        clearable
        :append-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
        @click:append="() => (visible = !visible)"
        :type="visible ? 'text' : 'password'"
        :rules="[validateNotEmpty]"
      >
      </v-text-field>

      <v-row>
        <v-checkbox
          class="ma-0 pa-0 ml-4"
          v-model="rememberMe"
          label="Remember me"
          :ripple="false"
        ></v-checkbox>
        <v-spacer />
        <p class="mr-5">
          <router-link
            :to="{ name: 'ForgotPassword' }"
            data-testid="forgot-password"
          >
            Forgot password?
          </router-link>
        </p>
      </v-row>

      <v-btn
        class="mt-4"
        elevation="6"
        large
        color="primary"
        type="submit"
        :disabled="!isLoginFormValid"
        data-testid="login"
      >
        Login
      </v-btn>

      <v-row class="mt-8 justify-center">
        <p>
          <router-link :to="{ name: 'Register' }" data-testid="create-account">
            Create account
          </router-link>
        </p>

        <p class="mx-2">|</p>

        <a href="https://www.bio-conversion.org/" target="_blank">
          Learn More
        </a>
      </v-row>
    </v-form>

    <!--2FA form -->
    <v-form
      ref="authForm"
      v-if="show2FA"
      @submit.prevent="onCodeEntered"
      v-model="is2FAFormValid"
    >
      <p class="mt-n4"><b>Two-Factor Authentication (2FA)</b></p>
      <div class="mt-12 mx-3">
        <p v-if="mfaType == 'EMAIL_MFA'">
          Please enter the 2FA code sent to your secondary email:
        </p>
        <p v-if="mfaType == 'EMAIL_MFA'">
          Code expires in: {{ timerMinutes }} m
        </p>
        <p v-else-if="mfaType == 'SOFTWARE_TOKEN_MFA'">
          Please enter the 2FA code from your authenticator app:
        </p>

        <v-text-field
          class="mt-8"
          label="2FA Code*"
          v-model="authCode"
          outlined
          clearable
          :rules="[validateIsNumber]"
        >
        </v-text-field>
        <v-btn
          v-if="mfaType == 'EMAIL_MFA'"
          color="primary"
          @click="sendMfaEmailCode"
          large
          elevation="6"
          class="mt-3"
          :disabled="timerCount > 0"
        >
          Resend Code
        </v-btn>
        <div style="margin-top: 74px">
          <v-btn
            text
            @click="backToLogin"
            large
            elevation="6"
            class="mx-2 mb-4"
          >
            Back
          </v-btn>
          <v-btn
            color="primary"
            type="submit"
            large
            :disabled="!is2FAFormValid"
            elevation="6"
            class="mb-4"
          >
            Verify
          </v-btn>
        </div>
      </div>
    </v-form>
  </v-container>
</template>

<script>
import UserPool from '@/plugins/UserPool';
import {
  validateNotEmpty,
  validateIsNumber,
} from '@/utilities/validationRules';
import { CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
const REGION = 'us-west-2';
let cognitoProviderName =
  'cognito-idp.' +
  REGION +
  '.amazonaws.com/' +
  process.env.VUE_APP_COGNITO_POOL_ID;
export default {
  name: 'LoginDefault',
  data: () => {
    return {
      timerCount: 900,
      timerEnabled: false,
      // visual state
      visible: false,
      showLogin: true,
      show2FA: false,
      // login data
      email: '',
      password: '',
      rememberMe: false,
      isLoginFormValid: false,
      authCode: '',
      emailMfaCode: '',
      secondaryEmail: '',
      mfaType: '',
      is2FAFormValid: false,
      // reference to user so we don't have to authenticate the user twice
      user: {
        type: CognitoUser,
      },
    };
  },
  computed: {
    timerMinutes() {
      return Math.round(this.timerCount / 60);
      console.log('test');
    },
  },
  watch: {
    rememberMe(val) {
      localStorage.setItem('rememberMe', val.toString());
    },
    //Incrementing the timer
    timerEnabled(value) {
      if (value) {
        setTimeout(() => {
          this.timerCount--;
        }, 1000);
      }
    },
    timerCount: {
      handler(value) {
        if (this.value <= 0) {
          this.timerEnabled = false;
        }
        if (value > 0 && this.timerEnabled) {
          setTimeout(() => {
            this.timerCount--;
          }, 1000);
        }
      },
      immediate: true, // This ensures the watcher is triggered upon creation
    },
  },
  methods: {
    validateNotEmpty,
    validateIsNumber,
    backToLogin() {
      this.showLogin = true;
      this.show2FA = false;
      this.$refs.authForm.reset();
    },
    async sendMfaEmailCode() {
      this.timerEnabled = true;
      this.timerCount = 900;
      //Initializing the lambda client
      const lambda = new LambdaClient({
        region: REGION,
        credentials: fromCognitoIdentityPool({
          client: new CognitoIdentityClient({ region: REGION }),
          identityPoolId: process.env.VUE_APP_COGNITO_IDENTITY_POOL_ID_LAMBDA,
          logins: {
            [`${cognitoProviderName}`]:
              this.user.signInUserSession.idToken.jwtToken,
          },
        }),
      });
      //generating random code and setting the email params
      const min = 10000;
      const max = 99999;
      this.emailMfaCode = Math.floor(Math.random() * (max - min)) + min;
      this.secondaryEmail =
        this.user.signInUserSession.idToken.payload[
          'custom:alternate-email-2fa'
        ];
      const params = {
        FunctionName: 'email2FA',
        InvocationType: 'RequestResponse',
        LogType: 'None',
        Payload: JSON.stringify({
          email: this.secondaryEmail,
          code: this.emailMfaCode,
        }),
      };
      //Calling the lambda function that sends email out
      try {
        await lambda.send(new InvokeCommand(params));
      } catch (error) {
        console.log(error);
      }
    },
    onLogin() {
      this.$emit('testOnLogin');

      this.user = new CognitoUser({
        Username: this.email,
        Pool: UserPool,
      });

      const authDetails = new AuthenticationDetails({
        Username: this.email,
        Password: this.password,
      });

      this.user.authenticateUser(authDetails, {
        onSuccess: (session) => {
          console.log(session); // DEBUG

          // check the user attributes to see if we should prompt the user to set up mfa
          if (
            !session.idToken.payload['custom:prompt-mfa'] ||
            session.idToken.payload['custom:prompt-mfa'] == 'true'
          ) {
            this.$emit('toggle-on-mfa', authDetails);
          } else {
            // use session storage or local storage depending on whether the user chooses to be remembered
            const promptEmailMfa =
              this.user.signInUserSession.idToken.payload[
                'custom:prompt-email-mfa'
              ];
            if (promptEmailMfa == 'true') {
              this.sendMfaEmailCode();
              this.show2FA = true;
              this.showLogin = false;
              this.mfaType = 'EMAIL_MFA';
              return;
            }
            if (this.rememberMe) {
              localStorage.setItem('authenticated', 'true');
            } else {
              sessionStorage.setItem('authenticated', 'true');
            }
            // keep this so that we do not get unnecessary errors when going from login page to TnC page
            // https://stackoverflow.com/questions/62223195/vue-router-uncaught-in-promise-error-redirected-from-login-to-via-a
            this.$router.push({ name: 'Dashboard' }).catch(() => {});
          }
        },
        onFailure: (err) => {
          console.error('Login failed: ', err); // DEBUG
          const notification = {
            type: 'error',
            message: err.message,
          };
          this.$store.dispatch('addNotification', notification);
          if (err.code === 'UserNotConfirmedException') {
            this.$emit('toggle-on-confirm', authDetails);
          }
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          console.log('New password required: ', userAttributes); // DEBUG
          console.log('Required attributes: ', requiredAttributes); // DEBUG

          // if the required attributes list is not empty, then there is account setup required
          if (requiredAttributes.length > 0) {
            this.$router.push({
              name: 'Register',
              params: { user: this.user }, // user object must be passed as a param to retain reference to the current session
            });
            const notification = {
              type: 'warning',
              message: 'Account setup required',
            };
            this.$store.dispatch('addNotification', notification);
          } else {
            this.$router.push({
              name: 'ResetPassword',
              params: { user: this.user }, // user object must be passed as a param to retain reference to the current session
            });
            const notification = {
              type: 'warning',
              message: 'New password required',
            };
            this.$store.dispatch('addNotification', notification);
          }
        },
        mfaRequired: (challengeName, challengeParameters) => {
          console.log(challengeName);
          console.log(challengeParameters);
          // show the 2FA form
          this.showLogin = false;
          this.show2FA = true; //This is temporary in disabling 2FA

          this.mfaType = challengeName;
        },
        totpRequired: (challengeName, challengeParameters) => {
          console.log(challengeName);
          console.log(challengeParameters);
          // show the 2FA form
          this.showLogin = false;
          this.show2FA = true;
          this.mfaType = challengeName;
        },
      });
    },
    onCodeEntered() {
      if (this.mfaType == 'EMAIL_MFA') {
        if (this.authCode != this.emailMfaCode) {
          const notification = {
            type: 'error',
            message: 'Check your authentication code again',
          };
          this.$store.dispatch('addNotification', notification);
          return;
        }
        if (this.timerCount <= 0) {
          const notification = {
            type: 'error',
            message: 'Code has expired',
          };
          this.$store.dispatch('addNotification', notification);
          return;
        }
        if (this.rememberMe) {
          localStorage.setItem('authenticated', 'true');
        } else {
          sessionStorage.setItem('authenticated', 'true');
        }
        // keep this so that we do not get unnecessary errors when going from login page to TnC page
        // https://stackoverflow.com/questions/62223195/vue-router-uncaught-in-promise-error-redirected-from-login-to-via-a
        this.$router.push({ name: 'Dashboard' }).catch(() => {});
      } else {
        this.user.sendMFACode(
          this.authCode,
          {
            onSuccess: (session) => {
              // mark user as authenticated and log them in
              if (this.rememberMe) {
                localStorage.setItem('authenticated', 'true');
              } else {
                sessionStorage.setItem('authenticated', 'true');
              }
              // keep this so that we do not get unnecessary errors when going from login page to TnC page
              // https://stackoverflow.com/questions/62223195/vue-router-uncaught-in-promise-error-redirected-from-login-to-via-a
              this.$router.push({ name: 'Dashboard' }).catch(() => {});
            },
            onFailure: (err) => {
              console.log(err);

              const notification = {
                type: 'error',
                message: err.message,
              };
              this.$store.dispatch('addNotification', notification);
            },
          },
          this.mfaType,
        );
      }
    },
  },
};
</script>
